import IconMeter from '@assets/icons/meter.svg?react';
import IconMoon from '@assets/icons/moon.svg?react';
import IconNav from '@assets/icons/nav.svg?react';
import IconSettings from '@assets/icons/settings.svg?react';
import IconSun from '@assets/icons/sun.svg?react';
import IconInfo from '@assets/icons/info.svg?react';
import {
 AppBar,
 Box,
 IconButton,
 Toolbar,
 useColorScheme,
 useTheme,
} from '@mui/material';
import type { Dispatch, SetStateAction } from 'react';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../redux/hooks';
import type { RootState } from '../../../redux/store';
import layoutSizes from '../../../utils/layoutSizes';
import HeaderSubNav from './HeaderSubNav';
import UserWidget from './UserWidget';
import RoleChecker from '../RoleChecker';
import UserGuideModal from '../UserGuideModal';

const Header = ({
 isMenuSidebarOpen,
 setIsMenuSidebarOpen,
}: {
 isMenuSidebarOpen: boolean;
 setIsMenuSidebarOpen: Dispatch<SetStateAction<boolean>>;
}) => {
 const { mode, systemMode, setMode } = useColorScheme();
 const resolvedMode = mode === 'system' ? systemMode : mode;
 const theme = useTheme();
 const navigate = useNavigate();
 const location = useLocation();
 const { t } = useTranslation();
 const isAdminPanel = location.pathname.includes('/admin/');
 const headerTitle = useAppSelector(
  (state: RootState) => state.header.headerTitle,
 );
 const [isGuideOpen, setIsGuideOpen] = useState(false);

 const handleAdminButtonClick = () => {
  if (isAdminPanel) {
   navigate('/results');
   return;
  }
  navigate('/admin/users');
 };

 return (
  <AppBar
   elevation={0}
   position="fixed"
   sx={{
    height: layoutSizes.headerHeight,
    width: isMenuSidebarOpen
     ? { xs: '100%', md: `calc(100% - ${layoutSizes.sidebarWidth}px)` }
     : { xs: '100%', md: 'calc(100% - 76px)' },
    ml: isMenuSidebarOpen
     ? { xs: 0, md: `${layoutSizes.sidebarWidth}px` }
     : { xs: '0px', md: '76px' },
    boxShadow: 'none',
    background: theme.palette.kemek.bg2,
    borderBottom: `1px solid ${theme.palette.kemek.lightestGray}`,
   }}
  >
   <Toolbar sx={{ height: '100%' }}>
    <IconButton
     size="large"
     edge="start"
     color="inherit"
     title={t('global.toggleNavigationMenu')}
     aria-label={t('global.toggleNavigationMenu')}
     sx={{ mr: 2, width: 44, height: 44 }}
     onClick={() => setIsMenuSidebarOpen((prevState) => !prevState)}
    >
     <IconNav style={{ color: theme.palette.kemek.darkestGray }} />
    </IconButton>

    <HeaderSubNav headerTitle={headerTitle} />

    {/* <Search /> */}
    <Box
     sx={{
      gap: { xs: '24px', md: '32px' },
      display: 'flex',
      alignItems: 'center',
      marginLeft: 'auto',
     }}
    >
     {/* <Box>localStorage: {lsSize()}</Box> */}
     {/* <IconButton
      size="large"
      sx={{ p: '4px', height: 24, display: { xs: 'block', md: 'none' } }}
      edge="start"
      color="inherit"
      aria-label="menu"
     >
      <IconMeter />
     </IconButton> */}
     {/* <Box
						sx={{
							display: { xs: 'none', md: 'flex' },
							alignItems: 'center',
							gap: '20px',
						}}
					>
						<ScalesWidget />
						<ScalesWidget />
					</Box> */}
     <UserWidget />
     <IconButton
      onClick={() => setIsGuideOpen(true)}
      size="large"
      title={t('userGuide.openGuide')}
      sx={{
       p: '0px',
       width: 24,
       height: 24,
       ml: 0,
       display: { xs: 'none', md: 'flex' },
      }}
      edge="start"
      color="inherit"
      aria-label={t('userGuide.openGuide')}
     >
      <IconInfo
       style={{
        color: theme.palette.kemek.darkestGray,
        width: 24,
        height: 24,
       }}
      />
     </IconButton>
     <IconButton
      onClick={() => setMode(resolvedMode === 'dark' ? 'light' : 'dark')}
      size="large"
      title={t('global.toggleColorMode')}
      sx={{
       p: '0px',
       width: 24,
       height: 24,
       ml: 0,
       display: { xs: 'none', md: 'flex' },
      }}
      edge="start"
      color="inherit"
      aria-label={t('global.toggleColorMode')}
     >
      {resolvedMode === 'dark' ? (
       <IconSun
        style={{
         color: theme.palette.kemek.darkestGray,
         width: 24,
         height: 24,
        }}
       />
      ) : (
       <IconMoon
        style={{
         color: theme.palette.kemek.darkestGray,
         width: 24,
         height: 24,
        }}
       />
      )}
     </IconButton>
     <RoleChecker resource="Users" action="GetAll">
      <IconButton
       onClick={handleAdminButtonClick}
       size="large"
       title={
        isAdminPanel ? t('global.backToResults') : t('global.openAdminPanel')
       }
       sx={{
        p: '0px',
        width: 36,
        height: 36,
        ml: 0,
        display: { xs: 'none', md: 'flex' },
        ...(isAdminPanel && {
         backgroundColor: theme.palette.kemek.blue,
         '&:hover': {
          backgroundColor: theme.palette.kemek.blue,
         },
        }),
       }}
       edge="start"
       color="inherit"
       aria-label={
        isAdminPanel ? t('global.backToResults') : t('global.openAdminPanel')
       }
      >
       <IconSettings
        style={{
         color: isAdminPanel ? '#fff' : theme.palette.kemek.darkestGray,
         width: 24,
         height: 24,
        }}
       />
      </IconButton>
     </RoleChecker>
    </Box>
   </Toolbar>
   <UserGuideModal isOpen={isGuideOpen} onClose={() => setIsGuideOpen(false)} />
  </AppBar>
 );
};

export default Header;
